import { useMemo, useState } from 'react';
import { DropdownMenu, FormActionType } from 'framework/ui/menu';
import { EMPLOYEE_DOCUMENT_STAGES_ALIAS } from '../../constants';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useShowContext } from 'framework/show';
import { Button } from 'framework/ui/mui-builder';
import MenuItem from '@mui/material/MenuItem/MenuItem';
import { capitalize } from 'lodash';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import { DEFAULT_ERROR_MESSAGE } from 'framework/form';
import { Modal } from 'framework/ui/ui-builder';
import AnnulEmployeeDocumentAction from './actions/AnnulEmployeeDocumentAction';
import { DocumentStorageItemType } from 'framework/documentStorage';
import { useDocumentStorageContext } from 'framework/documentStorage';
import { getEmployeeDocumentStageOptions } from '../../stageRules';

const MoveToStageEmployeeDocButton = () => {
  const { data, setData } = useShowContext<DocumentStorageItemType>();
  const { addMessage } = useMessageService();
  const { editDocument } = useDocumentStorageContext();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState<Nullable<FormActionType>>(null);

  const documentData = data.getData();
  const { section, fileType, stage: currentStage } = data.getValueBySource('metadata') || {};

  const stageOptions = useMemo(() => getEmployeeDocumentStageOptions(section, fileType), [section, fileType]);

  const moveStageSubmit = async (newStage: string) => {
    try {
      await editDocument({
        ...documentData,
        metadata: {
          ...documentData.metadata,
          stage: newStage,
        },
      });

      setData!({
        ...documentData,
        metadata: {
          ...documentData.metadata,
          stage: newStage,
        },
      });
    } catch {
      addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalConfig(null);
  };

  if (currentStage === EMPLOYEE_DOCUMENT_STAGES_ALIAS.ANNULLED) {
    return null;
  }

  return (
    <>
      <DropdownMenu
        popupId="move-employee-doc-stage"
        iconButton={
          <Button link sx={{ fontSize: '12px', fontWeight: '400', mt: '2px' }}>
            Move to stage
            <KeyboardArrowDownIcon sx={{ fontSize: '14px' }} />
          </Button>
        }
      >
        {Object.entries(stageOptions).map(([stageKey, stage]) => {
          if (stageKey === currentStage) {
            return null;
          }

          return (
            <MenuItem
              key={stage}
              sx={{
                fontSize: '12px',
              }}
              onClick={() => {
                if (EMPLOYEE_DOCUMENT_STAGES_ALIAS.ANNULLED === stageKey) {
                  setModalOpen(true);
                  setModalConfig({
                    Component: AnnulEmployeeDocumentAction,
                    title: 'Are you sure?',
                    afterSubmit: {
                      execute: updatedEnity => {
                        setData!({
                          ...documentData,
                          metadata: {
                            ...documentData.metadata,
                            annullingReason: updatedEnity.annullingReason,
                            stage: EMPLOYEE_DOCUMENT_STAGES_ALIAS.ANNULLED,
                          },
                        });
                      },
                    },
                  });
                } else {
                  moveStageSubmit(stageKey);
                }
              }}
            >
              {capitalize(String(stage).toLowerCase())}
            </MenuItem>
          );
        })}
      </DropdownMenu>

      {modalConfig && (
        <Modal
          isOpen={modalOpen}
          title={modalConfig.title}
          onToggle={closeModal}
          hasCancelButton
          className="form-modal"
          modalSize="sm"
          paperStyles={{ backgroundColor: 'var(--mui-palette-background-paper)' }}
        >
          <modalConfig.Component modalOpen={modalOpen} closeModal={closeModal} afterSubmit={modalConfig.afterSubmit} />
        </Modal>
      )}
    </>
  );
};

export default MoveToStageEmployeeDocButton;
